import React from 'react';
import { Link } from 'react-router-dom';

const UserAgreement: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">User Agreement</h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString('en-US')}
            </p>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Important Notice:</strong> This User Agreement contains provisions that govern the resolution of disputes between GiftMkr and you, including an arbitration agreement, class action waiver, and jury trial waiver that affect your rights. Please read carefully.
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                This User Agreement ("Agreement") is between you and GiftMkr regarding the use of our website, mobile applications, and related services.
                By accessing or using the GiftMkr website, you agree to comply with all terms and conditions of this Agreement.
              </p>
              <p className="text-gray-700 mb-4">
                If you do not agree with any terms of this Agreement, please do not use our services. GiftMkr reserves the right to modify this Agreement at any time,
                and the modified agreement will be posted on the website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Account Registration and Security</h2>
              <p className="text-gray-700 mb-4">
                When registering for a GiftMkr account, you must provide accurate personal information, including a valid email address and password.
                You are responsible for maintaining the security and confidentiality of your account information.
              </p>
              <p className="text-gray-700 mb-4">
                You agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Immediately notify us of any unauthorized use of your account</li>
                <li>Safely log out of your account after each use</li>
                <li>Take responsibility for all activities under your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Conduct Guidelines</h2>
              <p className="text-gray-700 mb-4">
                When using GiftMkr services, you agree not to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Upload, post, or transmit any content that is illegal, harmful, threatening, abusive, harassing, defamatory, obscene, or infringes on others' rights</li>
                <li>Impersonate others or misrepresent your identity</li>
                <li>Infringe on any patents, trademarks, trade secrets, copyrights, or other intellectual property rights</li>
                <li>Distribute viruses, malicious code, or programs that interfere with system operations</li>
                <li>Engage in any activities that may harm GiftMkr or other users' interests</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                The GiftMkr website and its content (including but not limited to text, images, designs, software) are protected by intellectual property laws.
                You may not copy, distribute, or create derivative works without explicit permission.
              </p>
              <p className="text-gray-700 mb-4">
                You retain the original rights to your uploaded content, but grant GiftMkr a license to use your content in providing our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Products and Services</h2>
              <p className="text-gray-700 mb-4">
                GiftMkr is an online customization platform where users can:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Purchase customized products</li>
                <li>Create and sell their own designs</li>
                <li>Use our design tools</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Product prices, inventory, and availability may change at any time. We reserve the right to refuse or cancel any orders.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Privacy Protection</h2>
              <p className="text-gray-700 mb-4">
                We value your privacy. Please refer to our Privacy Notice to understand how we collect, use, and protect your personal information.
                Using our services indicates your agreement to our Privacy Notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                GiftMkr provides services "as is" without any express or implied warranties.
                We do not guarantee that services will be uninterrupted, timely, secure, or error-free.
              </p>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, GiftMkr shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                Any disputes related to this Agreement should be resolved through friendly negotiation. If negotiation fails, both parties agree to resolve disputes through arbitration.
                Arbitration will be conducted in your jurisdiction, subject to applicable laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Agreement Termination</h2>
              <p className="text-gray-700 mb-4">
                You or GiftMkr may terminate this Agreement at any time. After termination, you will no longer be able to use our services,
                but existing rights and obligations will not be affected.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Agreement or need assistance, please contact us through the following methods:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> service@giftmkr.com<br />
                  <strong>Website:</strong> www.giftmkr.com
                </p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <p className="text-sm text-gray-500 mb-4 sm:mb-0">
                © {new Date().getFullYear()} GiftMkr. All rights reserved.
              </p>
              <div className="flex space-x-4">
                <Link 
                  to="/privacy-notice" 
                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Privacy Notice
                </Link>
                <Link 
                  to="/" 
                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAgreement;
